import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import {
  Datagrid,
  downloadCSV,
  List,
  TextField,
  useGetOne,
  useListContext,
  useRecordContext,
  useRefresh,
} from "react-admin";
import sendRequest from "../dataProvider/sendRequest";
import CustomCopyProductLinkIcon from "../unit/CustomCopyProductLinkIcon";
import CustomDateField from "../unit/CustomDateField";
import CustomEditLinkIcon from "../unit/CustomEditLinkIcon";
import CustomEditRemoveIcon from "../unit/CustomEditRemoveIcon";
import formatDate from "../unit/formatDate";
import PostPagination from "../unit/PostPagination";
import SwitchIcon from "../unit/svgIcon/SwitchIcon";
import SwitchOnIcon from "../unit/svgIcon/SwitchOnIcon";
import useNotify from "../unit/useNotify";
import ProductListActions from "./ProductListActions";
import ProductListAside from "./ProductListAside";
import { getProductListFilter } from "./ProductListFilter";
import { hmLightTheme } from "../themes/hmTheme";
import CustomLoading from "../unit/CustomLoading";
import getFiltersFromURL from "../unit/getFiltersFromURL";
import HmLoading from "../unit/HmLoading";
import NoResults from "../unit/NoResults";
import NoPermission from "../unit/NoPermission";
import CustomProductHistoryIcon from "../unit/CustomProductHistoryIcon";
const CustomStatusField = ({ ...props }) => {
  let record = useRecordContext();
  if (!record) return null;
  let status = "Incomplete";
  if (record.iStatus == 1) {
    return <Typography>Active</Typography>;
  } else if (record.iStatus == 2) {
    return <Typography>Removed</Typography>;
  } else if (record.iStatus == 0) {
    return <Typography sx={{ color: "red" }}>Inactive</Typography>;
  }
};
const CustomProductNameField = ({ ...props }) => {
  let record = useRecordContext();
  if (!record) return null;
  return record?.sProductName;
};
const CustomProductIdField = ({ ...props }) => {
  let record = useRecordContext();
  if (!record) return null;
  return (
    <Typography
      id={"product-list-link" + record?.id}
      className={props?.className}
    >
      {record?.id}
    </Typography>
  );
};
const ProductCreatedDate = ({ ...props }) => {
  const record = useRecordContext();
  if (!record) return null;

  if (record?.createdLogDate) {
    return formatDate(record.createdLogDate);
  } else if (record?.created_at) {
    return formatDate(record.created_at);
  }
  return "";
};
const ProductCreatedBy = ({ ...props }) => {
  const record = useRecordContext();
  if (!record) return null;

  if (record?.createdBy) {
    return record.createdBy;
  } else if (record?.createdByBankerId) {
    return "Banker#" + record.createdByBankerId;
  }
  return "";
};
const ProductList = () => {
  const refresh = useRefresh();
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const [record, setRecord] = React.useState<any>(null);
  const [listKey, setListKey] = React.useState(0);
  const permissions =
    localStorage.getItem("bbaccess")?.toString().split(",") || [];
  if (!permissions.includes("personal_products")) {
    return <NoPermission />;
  }
  const { data, isLoading, error } = useGetOne("total/product", { id: 1 });
  React.useEffect(() => {
    setRecord(data);
  }, [data]);

  if (error) {
    errorNotify("Error loading Product " + error);
  }
  if (isLoading) {
    // return <Typography>Product List Loading</Typography>;
    return <HmLoading />;
  }
  const total = record ? record.total : 0;
  const totalSubTypeData = record ? record.returnSubTypeData : 0;

  const CustomActionField = ({ ...props }) => {
    let record = useRecordContext();
    if (!record) return null;
    const handleChangeStatus = () => {
      let newStatus = record.iStatus == 1 ? 0 : 1; // Toggle status

      // Update the record status in the database
      var reqUrl = "products/actions/changeStatus";
      var response = sendRequest(reqUrl, {
        id: record.id,
        data: {
          status: newStatus,
        },
      });
      response
        .then((res) => {
          if (res.code == 200) {
            successNotify("Status changed successfully");
            setListKey((prev) => prev + 1);
            refresh();
          } else {
            errorNotify("Failed to change status! " + res?.message);
          }
        })
        .catch((error) => {
          errorNotify("Error changing status: " + error.message);
        });
    };
    const handleRemove = () => {
      // Update the record status in the database
      var reqUrl = "products/actions/changeStatus";
      var response = sendRequest(reqUrl, {
        id: record.id,
        data: {
          status: 2,
        },
      });
      response
        .then((res) => {
          if (res.code == 200) {
            successNotify("Delete successfully");
            setListKey((prev) => prev + 1);
            refresh();
          } else {
            errorNotify("Failed to delete");
          }
        })
        .catch((error) => {
          errorNotify("Error delete product: " + error.message);
        });
    };
    return (
      <>
        {/* <CustomEditLinkIcon to={"/products/" + record?.id} /> */}
        <CustomEditLinkIcon
          to={"/products/" + record?.id}
          // status={record?.iStatus}
          label="Edit product settings"
        />
        <Tooltip
          title={
            `` + (record?.iStatus == 1 ? "Deactivate" : "Activate") + ` product`
          }
          placement="left"
        >
          <IconButton
            size="small"
            onClick={handleChangeStatus}
            sx={{ marginLeft: "5px", p: 1 }}
          >
            {record?.iStatus == 1 ? (
              <SwitchOnIcon color="red" />
            ) : (
              <SwitchIcon color="green" />
            )}
          </IconButton>
        </Tooltip>
        <CustomCopyProductLinkIcon
          id={record?.id}
          // disabled={record?.iStatus != 1}
          label="Duplicate product"
        />
        <CustomEditRemoveIcon onClick={handleRemove} />
        <CustomProductHistoryIcon
          id={record?.id}
          productName={record?.sProductName}
          label="Product history"
        />
      </>
    );
  };
  const customProductNewExporter = (records: any) => {
    let filterStatus = "";
    if (records.length > 0) {
      filterStatus = records[0].sStatus;
    }
    let data = [];
    let title = {};

    data = records.map((record: any) => {
      const { id, sProductName, sSubType, updated_at, dActiveDate, iStatus } =
        record;
      return {
        id: id,
        sProductName: sProductName,
        // sSubType: sSubType,
        dActiveDate: formatDate(updated_at),
        updated_at: formatDate(updated_at),
        updatedBy: record.updatedBy,
        createdLogDate: formatDate(record.createdLogDate),
        createdBy: record.createdBy || record.createdByBankerId,
        iStatus: iStatus == 1 ? "Active" : "Inactive",
      };
    });
    title = {
      id: "Product #",
      sProductName: "Product Name",
      // sSubType: "Sub Type",
      dActiveDate: "Last Activated",
      updated_at: "Last Modified",
      updatedBy: "Modified By",
      createdLogDate: "Date Created",
      createdBy: "Created By",
      iStatus: "Status",
    };

    return {
      title: title,
      data: data,
      filterStatus: filterStatus,
    };
  };
  const customProductExporter = (records: any) => {
    const filterData = customProductNewExporter(records);
    //{{{
    const currentFilters = getFiltersFromURL();
    const filter_status = currentFilters?.status;
    const filter_curStatus = currentFilters?.curStatus;
    const filter_filerLoanTypeId = currentFilters?.filerLoanTypeId;
    const otherFilters = { ...currentFilters };
    delete otherFilters.status;
    delete otherFilters.curStatus;
    delete otherFilters.filerLoanTypeId;
    const filterString = Object.entries(otherFilters)
      .map(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (key == "id") {
            key = "Product #";
          }
          return `[${key} is "${value}"]`;
        }
        return "";
      })
      .filter(Boolean)
      .join("");

    console.log("Filter string:", filterString);
    ///}}}
    let csvTitle = filterData.title;
    let data = filterData.data;
    let filterStatus = filterData.filterStatus;
    const csvData = [{ ...csvTitle }, ...data];
    const csvDataStr = csvData.map((row) => Object.values(row).join(","));
    let filename = "Products";
    filename += customProductFilterText(
      filter_status,
      filter_curStatus,
      filter_filerLoanTypeId
    );
    if (filterString) {
      csvDataStr.push("");
      csvDataStr.push("Filters Applied:" + filterString);
    }
    downloadCSV(csvDataStr.join("\n"), filename);
  };
  const customProductFilterText = (
    status: any,
    curStatus: any,
    filerLoanTypeId: any
  ) => {
    let filterText = "";
    if (status == "unsecured") {
      filterText += "_Unsecured";
      if (filerLoanTypeId == 1) {
        filterText += "_Small and Simple";
      } else if (filerLoanTypeId == 2) {
        filterText += "_Employment Based";
      } else if (filerLoanTypeId == 19) {
        filterText += "_Purchase Finance";
      } else if (filerLoanTypeId == 3) {
        filterText += "_General Purpose";
      } else if (filerLoanTypeId == 10) {
        filterText += "_Refund Anticipation";
      }
    } else if (status == "secured") {
      filterText += "_Secured Loan";
      if (filerLoanTypeId == 5) {
        filterText += "_Mortgage";
      } else if (filerLoanTypeId == 6) {
        filterText += "_Purchase Car Loan";
      } else if (filerLoanTypeId == 11) {
        filterText += "_Refinance Car Loan";
      }
    } else if (status == "builder") {
      filterText += "_Credit Builder Loan";
      if (filerLoanTypeId == 4) {
        filterText += "_Single Term";
      } else if (filerLoanTypeId == 12) {
        filterText += "_Credit Building Ladder";
      }
    } else if (status == "revolvingcredit") {
      filterText += "_Revolving Credit";
      if (filerLoanTypeId == 13) {
        filterText += "_Secured Credit Card";
      }
    } else if (status == "smallbusiness") {
      filterText += "_Small Business Loan";
      if (filerLoanTypeId == 18) {
        filterText += "_Micro Loan";
      }
    } else if (status == "account") {
      filterText += "_Account";
      if (filerLoanTypeId == 7) {
        filterText += "_Depository Account";
      }
    }
    return filterText;
  };
  const DefaultProductList = () => {
    const { total, data, isPending, isLoading, isFetching } = useListContext();
    const CustomRowStyle = (record: any) => {
      if (!record) return {};

      if (record?.iStatus == 1) {
        return {
          backgroundColor: "#fff",
          "&.MuiTableRow-hover:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        };
      } else {
        return {
          // backgroundColor: "#ffdb9d30",
          // "&.MuiTableRow-hover:hover": {
          //   backgroundColor: "#ffdb9d60",
          // },
          backgroundColor: "#f5f5f5",
          "& td.MuiTableCell-root": {
            color: "gray",
          },
          "& td.MuiTableCell-root span": {
            color: "gray",
          },
          "& td.MuiTableCell-root a": {
            color: "gray",
          },
          "& td.MuiTableCell-root p": {
            color: "gray",
          },
        };
      }
    };
    return (
      <>
        <Datagrid
          optimized
          rowClick={false}
          empty={false}
          // rowClick={handleRowClick}
          size="small"
          id="table-products"
          rowSx={CustomRowStyle}
          className="hm-list-datagrid"
          sx={{
            ".MuiTableCell-root": {
              // padding:"6px 16px"
              padding: "6px",
            },
            ".RaDatagrid-headerCell:first-child": {
              borderTopLeftRadius: "0px",
            },
            ".RaDatagrid-headerCell:last-child": {
              borderTopRightRadius: "0px",
            },
            "& .column-groups": {
              md: { display: "none" },
              lg: { display: "table-cell" },
            },
            "& .Mui-active": {
              color: "gray",
            },
            "& .MuiTableSortLabel-root": {
              padding: "5px",
            },
            "& .RaDatagrid-headerCell": {
              color: "gray",
              fontSize: "1rem",
              backgroundColor: "#edf4fb",
              wordWrap: "break-word",
            },
            "& .MuiTableCell-paddingCheckbox": {
              display: "none",
            },
            "& .MuiTableCell-root.column-sType": {
              textTransform: "capitalize",
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            },
            "& .MuiTableCell-root.column-sSubType": {
              textTransform: "capitalize",
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .RaDatagrid-headerCell.column-action": {
              minWidth: "250px",
              fontWeight: 700,
            },
            "& .RaDatagrid-headerCell.column-id": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .RaDatagrid-headerCell.column-sProductName": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .RaDatagrid-headerCell.column-updatedBy": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .RaDatagrid-headerCell.column-createdBy": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-dActiveDate": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
            },
            "& .MuiTableCell-root.column-updatedLogDate": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
            },
            "& .MuiTableCell-root.column-createdLogDate": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
            },
            "& .MuiTableCell-root.column-action": {
              textAlign: "center",
            },
          }}
        >
          {/* <TextField source="id" label="Product #" className="numericText " /> */}
          <CustomProductIdField
            source="id"
            label="Product #"
            className="numericText "
          />
          {/* Type */}
          {/* <TextField source="sType" label="Type" /> */}
          {/* Sub Type */}
          {/* <TextField source="sSubType" label="Sub Type" /> */}

          <CustomProductNameField source="sProductName" label="Name" />
          {/* <TextField source="sTitle" label="Sub Type" /> */}

          {/* Date Activated */}
          <CustomDateField
            source="dActiveDate"
            label="Last Activated"
            onlyShowDate={true}
            cellClassName="dateText"
          />
          {/* last modified */}
          <CustomDateField
            // source="updated_at"
            source="updatedLogDate"
            label="Last Modified"
            onlyShowDate={true}
            cellClassName="dateText"
          />
          <TextField source="updatedBy" label="Modified By" />
          {/* <CustomDateField
            source="createdLogDate"
            label="Date Created"
            onlyShowDate={true}
            cellClassName="dateText"
          /> */}
          <ProductCreatedDate
            source="createdLogDate"
            label="Date Created"
            cellClassName="dateText"
          />
          <ProductCreatedBy source="createdBy" label="Created By" />

          {/* Status */}
          {/* <TextField source="iStatus" label="Status" /> */}
          {/* <CustomStatusField source="iStatus" label="Status" sortable={false} /> */}
          <CustomActionField source="action" sortable={false} label="Action" />
        </Datagrid>
        <CustomLoading />
      </>
    );
  };
  return (
    <>
      <List
        key={listKey}
        actions={<ProductListActions selected="product" />}
        // filters={ProductListFilter}
        filters={getProductListFilter()}
        aside={
          <ProductListAside totalData={total} subTotalData={totalSubTypeData} />
        }
        sort={{ field: "created_at", order: "DESC" }}
        exporter={customProductExporter}
        sx={{
          width: "100%",
          borderRadius: "0px",
          "& .MuiCard-root": {
            borderRadius: "0px",
          },
          "& .RaList-actions form": {
            flex: "0 1 350%",
          },
          "& .RaList-actions form .filter-field .MuiInputBase-input": {
            width: "230px",
          },
          "& .RaList-actions .RaFilterForm-filterFormInput:first-child .MuiInputBase-root::before":
            {
              display: "none",
            },
          "& .RaList-actions .filter-field:first-child .MuiFormControl-root": {
            marginLeft: 0,
          },
          // columns-name 200px
          "& .RaDatagrid-headerCell.column-sProductName": {
            width: "200px",
          },
        }}
        empty={
          <Typography className="no-results">
            <NoResults />
          </Typography>
        }
        emptyWhileLoading={false}
        loading={<HmLoading />}
        pagination={<PostPagination />}
        perPage={25}
      >
        <DefaultProductList />
      </List>
    </>
  );
};

// const QuickFilter = ({ label }: InputProps) => {
//   const translate = useTranslate();
//   return <Chip sx={{ mb: 1 }} label={translate(label as string)} />;
// };

// const ListActions = ({ isSmall }: any) => (
//   <TopToolbar>
//     {isSmall && <FilterButton />}
//     <SortButton fields={["reference", "sales", "stock"]} />
//     <CreateButton />
//     <ExportButton />
//   </TopToolbar>
// );

export default ProductList;
